// Shared colour legend — swatch + label per classification value, for any
// `NodeColoring` source (type, aspect or diff). Presentational: the host passes
// the entries (it already holds the coloring it built) and, optionally,
// hover/pin interaction so a row can highlight its member nodes on the board.
// Floating card, bottom-left, matching the board HUD chrome.
//
// Collapsible on every surface: a header (disclosure chevron on the LEFT + the
// title + the entry count) toggles the rows, collapsed by default so it stays a
// small pill until opened. `mobile` only changes the treatment — rounder card,
// finger padding, top-left anchor, and a scroll cap on a long list.

import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { useContextMenu, type ContextMenuItem } from "@archlang/ui";
import type { ColorLegendEntry } from "./types.js";
import { useTranslation } from "../../i18n";

export interface ColorLegendProps {
  readonly title?: string;
  readonly entries: readonly ColorLegendEntry[];
  /** Currently pinned entry id (sticky highlight). */
  readonly pinnedId?: string | null;
  /** Hover preview — passes the entry id, or null on leave. */
  readonly onHover?: (id: string | null) => void;
  /** Toggle a pinned entry. Omit to make rows non-interactive. */
  readonly onPin?: (id: string) => void;
  /** Extra positioning classes (default: bottom-left floating). */
  readonly className?: string;
  /** Phone treatment — see the header comment. */
  readonly mobile?: boolean;
}

export function ColorLegend({
  title,
  entries,
  pinnedId,
  onHover,
  onPin,
  className,
  mobile = false,
}: ColorLegendProps) {
  const { t } = useTranslation("colorLegend");
  const menu = useContextMenu();
  const [open, setOpen] = useState(false);
  if (entries.length === 0) return null;
  const interactive = Boolean(onHover || onPin);

  const itemsFor = (e: ColorLegendEntry): ContextMenuItem[] => [
    {
      label: t("menu.copyLabel"),
      onClick: () => void navigator.clipboard?.writeText(e.label),
    },
    ...(onPin
      ? [
          {
            label: pinnedId === e.id ? t("menu.unpin") : t("menu.pin"),
            separator: true,
            onClick: () => onPin(e.id),
          },
        ]
      : []),
  ];
  return (
    <div
      data-hud
      data-hud-region="legend"
      className={
        "pointer-events-auto max-w-56 text-xs " +
        (mobile
          ? "rounded-2xl border border-white/10 bg-slate-900/(--tint-strong) px-3 py-2 shadow-lg shadow-black/30 backdrop-blur-xl "
          : "rounded-lg border border-slate-700 bg-slate-900/(--tint-strong) px-3 py-2 shadow-lg backdrop-blur-md ") +
        (className ?? (mobile ? "absolute top-14 left-2 z-chrome" : "absolute bottom-3 left-3 z-chrome"))
      }
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={
          "flex w-full items-center gap-1.5 rounded-sm font-medium text-slate-300 " +
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 " +
          (mobile ? "py-1" : "py-0.5")
        }
      >
        <ChevronRight
          width={14}
          height={14}
          aria-hidden="true"
          className={"shrink-0 transition-transform duration-200 " + (open ? "rotate-90" : "")}
        />
        <span className="truncate text-slate-400">{title ?? t("defaultTitle")}</span>
        <span className="ml-auto tabular-nums text-slate-500">{entries.length}</span>
      </button>
      {open && (
      <ul
        className={"mt-1 space-y-0.5 " + (mobile ? "max-h-[45dvh] overflow-y-auto" : "")}
        onMouseLeave={() => onHover?.(null)}
      >
        {entries.map((e) => {
          const active = pinnedId === e.id;
          const swatch = (
            <>
              <span
                className={
                  "inline-block h-3 shrink-0 rounded-xs border border-white/20 " +
                  // A gradient (heat-ramp) row gets a wider swatch so the ramp reads.
                  (e.gradient ? "w-6" : "w-3")
                }
                style={{
                  background: e.gradient
                    ? `linear-gradient(90deg, ${e.gradient.from}, ${e.gradient.to})`
                    : e.color,
                }}
              />
              <span className="truncate">{e.label}</span>
              {e.gradient && (
                <span className="ml-auto shrink-0 tabular-nums text-slate-500">
                  {e.gradient.minLabel}–{e.gradient.maxLabel}
                </span>
              )}
            </>
          );
          return (
            <li key={e.id} onContextMenu={(ev) => menu.open(ev, itemsFor(e))}>
              {interactive ? (
                <button
                  type="button"
                  onMouseEnter={() => onHover?.(e.id)}
                  onClick={() => onPin?.(e.id)}
                  title={t("rowTitle")}
                  className={
                    "flex w-full items-center gap-2 rounded-sm text-left " +
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 " +
                    (mobile ? "px-2 py-2 " : "px-1.5 py-0.5 ") +
                    (active ? "bg-slate-700/(--tint-strong) text-white" : "text-slate-200 hover:bg-slate-800")
                  }
                >
                  {swatch}
                </button>
              ) : (
                <div
                  className={
                    "flex w-full items-center gap-2 text-slate-200 " +
                    (mobile ? "px-2 py-2" : "px-1.5 py-0.5")
                  }
                >
                  {swatch}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      )}
      {menu.node}
    </div>
  );
}
